import { Controller } from "@hotwired/stimulus"

/**
 * Admin Tool Status Controller
 *
 * Polls for status updates when an admin tool is running.
 * Automatically stops polling when the job completes.
 *
 * Usage:
 *   <div data-controller="admin-tool-status"
 *        data-admin-tool-status-url-value="/profile/admin-tools/elasticsearch/status"
 *        data-admin-tool-status-running-value="true"
 *        data-admin-tool-status-interval-value="5000">
 */
export default class extends Controller {
  static values = {
    url: String,
    running: Boolean,
    interval: { type: Number, default: 5000 }
  }

  connect() {
    if (this.runningValue) {
      this.startPolling()
    }
  }

  disconnect() {
    this.stopPolling()
  }

  runningValueChanged() {
    if (this.runningValue) {
      this.startPolling()
    } else {
      this.stopPolling()
    }
  }

  startPolling() {
    if (this.pollTimer) return

    this.pollTimer = setInterval(() => {
      this.fetchStatus()
    }, this.intervalValue)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async fetchStatus() {
    try {
      // Pass was_running=true so the server knows this is a polling request
      // expecting a running state. If the job just completed, server can show
      // a completion flash message.
      const url = new URL(this.urlValue, window.location.origin)
      url.searchParams.set('was_running', 'true')

      const response = await fetch(url.toString(), {
        headers: {
          "Accept": "text/vnd.turbo-stream.html",
          "X-Requested-With": "XMLHttpRequest"
        }
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
      }
    } catch (error) {
      console.error("Failed to fetch admin tool status:", error)
    }
  }
}
